import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CollectionKey } from "../../../netlify/functions/_shared/types";
import {
  getAllArtists,
  getAllDays,
  getAllStages,
  getAllPerformances,
  reloadFestivalData,
} from "../../services/dataService";
import { adminDeleteEntity } from "../../services/adminDataService";

interface Entity {
  id: string;
  name?: string;
  eventName?: string;
  artistId?: string;
  stageId?: string;
  dayId?: string;
  description?: string;
}

export const EntityListPage = () => {
  const { collection } = useParams<{ collection: string }>();
  const [data, setData] = useState<Entity[]>([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortReversed, setSortReversed] = useState(false);

  useEffect(() => {
    setSearchQuery("");
    setSortReversed(false);
    try {
      if (collection === "artists") setData(getAllArtists());
      else if (collection === "days") setData(getAllDays());
      else if (collection === "stages") setData(getAllStages());
      else if (collection === "performances") setData(getAllPerformances());
      else if (collection === "festivalInfo") {
        // festivalInfo is a single object, redirect to editor directly
        window.location.href = "/admin/festivalInfo/edit";
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [collection]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete ${id}?`)) return;
    try {
      await adminDeleteEntity(collection as CollectionKey, id);
      await reloadFestivalData(true);
      if (collection === "artists") setData(getAllArtists());
      else if (collection === "days") setData(getAllDays());
      else if (collection === "stages") setData(getAllStages());
      else if (collection === "performances") setData(getAllPerformances());
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  const filteredData = data.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();

    const searchString = [
      item.id,
      item.name,
      item.eventName,
      item.artistId,
      item.stageId,
      item.dayId,
      item.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchString.includes(query);
  });

  const displayData = sortReversed ? [...filteredData].reverse() : filteredData;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold font-['Unbounded'] capitalize text-white">
          {collection}
        </h1>
        <Link
          to={`/admin/${collection}/new`}
          className="bg-main text-background px-4 py-2 rounded-lg font-bold hover:opacity-90 hover:shadow-[0_0_10px_rgba(94,207,190,0.4)] transition-all"
        >
          Create New
        </Link>
      </div>

      {error && (
        <div className="text-red-400 font-mono p-3 bg-red-950/30 border border-red-900/50 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg">
        {/* Search & Sort Controls Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-center p-4 border-b border-border bg-background-accent/50">
          <div className="relative w-full sm:max-w-xs">
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background text-text border border-border focus:outline-none focus:ring-2 focus:ring-main focus:border-main rounded-md text-sm transition-all"
            />
            <div className="absolute left-3 top-2.5 text-muted">
              <svg
                role="img"
                aria-label="Search icon"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSortReversed(!sortReversed)}
            className="w-full sm:w-auto px-4 py-2 bg-background-accent border border-border rounded-md hover:bg-card-hover text-sm font-semibold flex items-center justify-center gap-2 transition duration-150 shadow-sm text-text cursor-pointer"
          >
            <span>Sort: {sortReversed ? "Reversed" : "Natural"}</span>
            <svg
              role="img"
              aria-label="Sort direction icon"
              className={`h-4 w-4 text-muted transition-transform duration-200 ${sortReversed ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              />
            </svg>
          </button>
        </div>

        <table className="w-full text-left">
          <thead className="bg-background-accent/80 border-b border-border text-muted">
            <tr>
              <th className="p-3">ID</th>
              <th className="p-3">Name/Info</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((item) => (
              <tr
                key={item.id}
                className="border-b border-border hover:bg-card-hover/40 transition-colors"
              >
                <td className="p-3 font-mono text-sm text-text-muted">{item.id}</td>
                <td className="p-3 text-text">
                  {item.name || item.eventName || `${item.artistId} @ ${item.stageId}`}
                </td>
                <td className="p-3 text-right space-x-3">
                  <Link
                    to={`/admin/${collection}/${item.id}`}
                    className="text-accent hover:text-accent-hover transition-colors font-semibold"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="text-pink-500 hover:text-pink-400 hover:underline transition-colors font-semibold cursor-pointer"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {displayData.length === 0 && (
              <tr>
                <td colSpan={3} className="p-4 text-center text-muted text-sm">
                  {searchQuery ? "No matching items found" : "No items found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EntityListPage;
